import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, alpha, Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { downloadAdReport, listAdAudits, runAdAudit } from 'api/adAudit';
import { apiGetAll } from 'api/service';
import { GREEN, MONO, SEV_COLOR, ago } from './scanUi';
import { HOW_TO, SEVERITIES, affectedOf, byPriority, domainOf, explainOf, riskShare, severityCounts, titleOf, topFixes } from './adLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const greenBtn = { fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } };
const FORMATS = [['pdf', 'PDF'], ['docx', 'Word'], ['xlsx', 'Excel'], ['csv', 'CSV']];
const ENGINES = [['auto', 'Auto (best available)'], ['bloodbash', 'BloodBash (certificates, Azure, attack paths)'], ['native', 'Built-in engine']];

const SevPill = ({ severity }) => (
  <Chip size="small" label={severity.toUpperCase()} sx={{ fontWeight: 700, fontSize: 11, borderRadius: 1.5, bgcolor: alpha(SEV_COLOR[severity] || '#64748b', 0.16), color: SEV_COLOR[severity] || 'text.secondary' }} />
);

// Affected accounts and the fix, shown when a weakness is opened.
function Detail({ f }) {
  const raw = f.raw_data || {};
  const affected = raw.affected || [];
  return (
    <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: alpha('#000', 0.2) }}>
      <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{explainOf(f)}</Typography>
      {affected.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" fontWeight={700}>Affected ({affectedOf(f)})</Typography>
          <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ mt: 0.6, maxHeight: 150, overflowY: 'auto' }}>
            {affected.slice(0, 200).map((a, i) => <Chip key={`${a}-${i}`} size="small" label={a} sx={{ fontFamily: MONO, fontSize: 11, height: 20 }} />)}
            {affected.length > 200 && <Chip size="small" variant="outlined" label={`+${affected.length - 200} more`} />}
          </Stack>
        </Box>
      )}
      {raw.remediation && <Alert severity="info" icon={false} sx={{ mt: 1.5 }}><strong>How to fix it:</strong> {raw.remediation}</Alert>}
    </Box>
  );
}

function Upload({ currentTargetId, engines, onDone, onError }) {
  const [files, setFiles] = useState([]);
  const [engine, setEngine] = useState('auto');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const add = (list) => setFiles((prev) => { const seen = new Set(prev.map((f) => `${f.name}:${f.size}`)); return [...prev, ...Array.from(list || []).filter((f) => !seen.has(`${f.name}:${f.size}`))]; });
  const available = (id) => id === 'auto' || id === 'native' || engines.find((e) => e.id === id)?.available !== false;
  const run = async () => {
    setBusy(true);
    try { const res = await runAdAudit(files, currentTargetId, engine); setFiles([]); onDone(res.scan_id); } catch (e) { onError(e?.message || 'The audit failed.'); }
    setBusy(false);
  };
  return (
    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 3 }}>
      <Box onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        sx={{ flex: '1.3 1 460px', border: '2px dashed', borderColor: over ? GREEN : 'divider', borderRadius: 3, p: 4, textAlign: 'center', bgcolor: alpha(GREEN, over ? 0.06 : 0.02) }}>
        <Box sx={{ width: 52, height: 52, mx: 'auto', mb: 1.5, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(GREEN, 0.14), color: GREEN, fontSize: 24 }}>↑</Box>
        <Typography fontWeight={700} fontSize={17}>Drop your collector files here</Typography>
        <Typography variant="body2" color="text.secondary">.json files or one .zip from SharpHound or BloodHound.py</Typography>
        <input ref={ref} type="file" multiple hidden accept=".json,.zip,application/json,application/zip" onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        <Button variant="outlined" color="inherit" onClick={() => ref.current.click()} sx={{ mt: 2 }}>Choose files</Button>
        {files.length > 0 && (
          <Stack gap={0.6} sx={{ mt: 2, textAlign: 'left' }}>
            {files.map((f) => (
              <Stack key={`${f.name}:${f.size}`} direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary">{(f.size / 1024).toFixed(1)} KB</Typography>
                <IconButton size="small" onClick={() => setFiles((p) => p.filter((x) => x !== f))}><RemoveIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Stack direction="row" gap={1.5} sx={{ mt: 1 }}>
              <TextField select size="small" value={engine} onChange={(e) => setEngine(e.target.value)} sx={{ flex: 1 }}>
                {ENGINES.map(([id, label]) => <MenuItem key={id} value={id} disabled={!available(id)}>{label}{available(id) ? '' : ' (not installed)'}</MenuItem>)}
              </TextField>
              <Button variant="contained" disabled={busy} onClick={run} sx={greenBtn}>{busy ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Run audit'}</Button>
            </Stack>
          </Stack>
        )}
      </Box>
      <Box sx={{ ...panel, flex: '1 1 320px', p: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 1.5 }}>How to collect the data</Typography>
        {HOW_TO.map((t, i) => (
          <Stack key={t} direction="row" gap={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', fontSize: 12.5, fontWeight: 700, bgcolor: alpha(GREEN, 0.14), color: GREEN }}>{i + 1}</Box>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{t}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

export default function AdAuditPage() {
  const { currentTargetId } = useFuzzScanTarget();
  const [audits, setAudits] = useState([]);
  const [engines, setEngines] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sev, setSev] = useState('all');
  const [openKey, setOpenKey] = useState(null);
  const [lang, setLang] = useState('en');
  const [menu, setMenu] = useState(null);
  const [toast, setToast] = useState(null);
  const say = (severity, message) => setToast({ severity, message });

  const loadAudits = async (select) => {
    if (!currentTargetId) { setAudits([]); return; }
    try {
      const d = await listAdAudits(currentTargetId);
      setAudits(d?.audits || []); setEngines(d?.engines || []);
      const pick = select || ((d?.audits || [])[0]?.id) || '';
      setActiveId(pick); if (!pick) setUploading(true);
    } catch { setAudits([]); }
  };
  useEffect(() => { setActiveId(''); setFindings([]); setUploading(false); loadAudits(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentTargetId]);
  const status = audits.find((a) => a.id === activeId)?.status;
  const anyRunning = audits.some((a) => a.status === 'running');
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => loadAudits(activeId), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRunning, activeId, currentTargetId]);
  useEffect(() => {
    if (!activeId || status === 'running' || status === 'failed') { setFindings([]); return undefined; }
    let live = true;
    setLoading(true); setSev('all'); setOpenKey(null);
    apiGetAll(`/api/ad-audit/weaknesses/?scan=${activeId}`).then((r) => live && setFindings(r)).catch((e) => say('error', e?.message || 'Could not load the weaknesses.')).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [activeId, status]);

  const audit = audits.find((a) => a.id === activeId);
  const counts = useMemo(() => severityCounts(findings), [findings]);
  const fixes = useMemo(() => topFixes(findings), [findings]);
  const all = useMemo(() => [...findings].filter((f) => sev === 'all' || f.severity === sev).sort(byPriority), [findings, sev]);
  const download = async (fmt) => {
    setMenu(null);
    try { await downloadAdReport(activeId, fmt, lang); } catch (e) { say('error', e?.message || 'The report could not be created.'); }
  };
  const hv = (audit?.high_value_targets || []).slice(0, 8);
  const c = audit?.counts || {};

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Active Directory audit</Typography>
          <Typography color="text.secondary">A short, ordered list of what to fix to remove the most risk</Typography>
        </Box>
        {currentTargetId && (
          <Stack direction="row" gap={1.2}>
            {audit && <Button variant="outlined" color="inherit" onClick={(e) => setMenu(e.currentTarget)} sx={{ height: 44, fontWeight: 600 }}>Download report ▾</Button>}
            <Button variant="contained" onClick={() => setUploading((u) => !u)} sx={{ height: 44, ...greenBtn }}>{uploading && audits.length ? 'Close' : 'New audit'}</Button>
          </Stack>
        )}
      </Stack>
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <Box sx={{ px: 2, py: 1 }}>
          <ToggleButtonGroup exclusive size="small" value={lang} onChange={(_, v) => v && setLang(v)}>
            <ToggleButton value="en" sx={{ px: 1.5, fontWeight: 700 }}>English</ToggleButton><ToggleButton value="fr" sx={{ px: 1.5, fontWeight: 700 }}>Français</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {FORMATS.map(([id, label]) => <MenuItem key={id} onClick={() => download(id)}>{label}</MenuItem>)}
      </Menu>

      {!currentTargetId && <Alert severity="info">Choose a target at the top of the page first.</Alert>}
      {currentTargetId && uploading && <Upload currentTargetId={currentTargetId} engines={engines} onDone={(id) => { setUploading(false); say('success', 'Files received. The analysis runs in the background and can take a few minutes.'); loadAudits(id); }} onError={(m) => say('error', m)} />}

      {audit && (
        <>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ ...panel, p: 2.2, mb: 2.5 }}>
            <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }}>{domainOf(audit)}</Typography>
            <TextField select size="small" value={activeId} onChange={(e) => setActiveId(e.target.value)} sx={{ minWidth: 230 }}>
              {audits.map((a) => <MenuItem key={a.id} value={a.id}>{ago(a.started_at)} · {a.status === 'running' ? 'analysing…' : a.status === 'failed' ? 'failed' : `${a.weaknesses} weaknesses`}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">{audit.engine === 'bloodbash' ? 'BloodBash' : 'Built-in engine'} · {(audit.files || []).length} file{(audit.files || []).length === 1 ? '' : 's'}</Typography>
            <Stack direction="row" gap={2.5} sx={{ ml: 'auto' }}>
              {[['users', c.users], ['computers', c.computers], ['groups', c.groups]].filter(([, n]) => n != null).map(([k, n]) => (
                <Typography key={k} variant="body2" color="text.secondary"><b style={{ fontFamily: MONO, color: 'inherit' }}>{Number(n).toLocaleString()}</b> {k}</Typography>
              ))}
            </Stack>
          </Stack>

          {status === 'running' && <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>Analysing your files. This can take a few minutes, and you can leave this page. The result appears here when it is ready.</Alert>}
          {status === 'failed' && <Alert severity="error" sx={{ mb: 2 }}>The audit could not be finished. {audit.error || 'Try uploading the files again.'}</Alert>}
          {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={26} sx={{ color: GREEN }} /></Box>}
          {!loading && status === 'completed' && !findings.length && <Alert severity="success">No weaknesses were found in this data.</Alert>}

          {findings.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start', mb: 3 }}>
              <Box sx={{ ...panel, borderColor: GREEN, flex: '2 1 600px', minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5 }}>
                  <Typography fontWeight={700} fontSize={17}>Fix these first</Typography>
                  <Typography variant="body2" color="text.secondary">Ordered by how much risk each one carries</Typography>
                </Box>
                {fixes.map((f, i) => (
                  <Stack key={f.id} direction="row" gap={2} onClick={() => setOpenKey(openKey === f.id ? null : f.id)} sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: alpha(GREEN, 0.03) } }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', fontWeight: 800, bgcolor: alpha(GREEN, 0.14), color: GREEN }}>{i + 1}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap"><Typography fontWeight={700}>{titleOf(f)}</Typography><SevPill severity={f.severity} /></Stack>
                      {f.raw_data?.remediation && <Typography variant="body2" sx={{ mt: 0.8, lineHeight: 1.6 }}>{f.raw_data.remediation}</Typography>}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>Affects {affectedOf(f)} · about {riskShare(f, findings)}% of this audit's risk</Typography>
                      {openKey === f.id && <Detail f={f} />}
                    </Box>
                  </Stack>
                ))}
              </Box>

              <Box sx={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {hv.length > 0 && (
                  <Box sx={{ ...panel, p: 2.5 }}>
                    <Typography fontWeight={600} sx={{ mb: 1 }}>High-value targets</Typography>
                    {hv.map((n) => <Typography key={n} variant="body2" sx={{ fontFamily: MONO, py: 0.8, borderTop: 1, borderColor: 'divider' }}>{n}</Typography>)}
                    {(audit.high_value_targets || []).length > hv.length && <Typography variant="caption" color="text.secondary">+{audit.high_value_targets.length - hv.length} more</Typography>}
                  </Box>
                )}
                <Box sx={{ ...panel, p: 2.5 }}>
                  <Typography fontWeight={600} sx={{ mb: 1.5 }}>All weaknesses</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {SEVERITIES.map((s) => (
                      <Box key={s} onClick={() => setSev(sev === s ? 'all' : s)} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.2, borderRadius: 2, cursor: 'pointer', border: 1, borderColor: sev === s ? SEV_COLOR[s] : 'divider' }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: SEV_COLOR[s] }} />
                        <Typography sx={{ fontFamily: MONO, fontWeight: 700 }}>{counts[s]}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{s}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {findings.length > 0 && (
            <Box sx={{ ...panel, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 2 }}>
                <Typography fontWeight={600}>{sev === 'all' ? 'Every weakness' : `${sev[0].toUpperCase()}${sev.slice(1)} weaknesses`}</Typography>
                <Typography variant="body2" color="text.secondary">{all.length} shown{sev !== 'all' && <Button size="small" onClick={() => setSev('all')}>Show all</Button>}</Typography>
              </Stack>
              {all.map((f) => (
                <Box key={f.id} onClick={() => setOpenKey(openKey === `a-${f.id}` ? null : `a-${f.id}`)} sx={{ px: 2.5, py: 1.6, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: alpha(GREEN, 0.03) } }}>
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 4, height: 30, borderRadius: 1, bgcolor: SEV_COLOR[f.severity] }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>{titleOf(f)}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.raw_data?.category || 'General'}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13 }}>{affectedOf(f)}</Typography>
                  </Stack>
                  {openKey === `a-${f.id}` && <Detail f={f} />}
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Box>
  );
}
